import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  deleteDoc,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function collectByUserId(collectionName, userId, emailLower) {
  const items = [];
  if (userId) {
    const byUid = await getDocs(query(collection(db, collectionName), where('userId', '==', userId)));
    byUid.forEach((d) => items.push({ id: d.id, ...d.data() }));
  }
  if (emailLower && collectionName === 'subscriptions') {
    const byEmail = await getDocs(query(collection(db, collectionName), where('email', '==', emailLower)));
    byEmail.forEach((d) => {
      if (!items.find((x) => x.id === d.id)) items.push({ id: d.id, ...d.data() });
    });
  }
  if (emailLower && collectionName === 'payments') {
    const byEmail = await getDocs(query(collection(db, collectionName), where('email', '==', emailLower)));
    byEmail.forEach((d) => {
      if (!items.find((x) => x.id === d.id)) items.push({ id: d.id, ...d.data() });
    });
  }
  return items;
}

async function collectMessages(userId) {
  const threadId = `student-${userId}`;
  const snap = await getDocs(query(collection(db, 'messages'), where('threadId', '==', threadId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Export full student archive (profile, payments, exams, progress, messages, etc.)
 */
export async function exportStudentArchive(userId, { purgeFromServer = false } = {}) {
  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) throw new Error('Student not found.');
  const user = { id: userSnap.id, ...userSnap.data() };
  const emailLower = (user.emailLower || user.email || '').toLowerCase();

  const [subscriptions, payments, progressSnap, trackingSnap, examSnap, messages, notifSnap] =
    await Promise.all([
      collectByUserId('subscriptions', userId, emailLower),
      collectByUserId('payments', userId, emailLower),
      getDoc(doc(db, 'userProgress', userId)),
      getDoc(doc(db, 'studyTracking', userId)),
      getDoc(doc(db, 'examAttempts', userId)),
      collectMessages(userId),
      getDocs(query(collection(db, 'notifications'), where('targetUserId', '==', userId))),
    ]);

  const threadSnap = await getDoc(doc(db, 'messageThreads', `student-${userId}`));

  const archive = {
    exportedAt: new Date().toISOString(),
    version: 1,
    user,
    subscriptions,
    payments,
    userProgress: progressSnap.exists() ? progressSnap.data() : null,
    studyTracking: trackingSnap.exists() ? trackingSnap.data() : null,
    examAttempts: examSnap.exists() ? examSnap.data() : null,
    messages,
    messageThread: threadSnap.exists() ? { id: threadSnap.id, ...threadSnap.data() } : null,
    notifications: notifSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
  };

  const safeName = (user.displayName || user.email || userId).replace(/[^\w.-]+/g, '_');
  downloadJson(`sba-student-${safeName}-${Date.now()}.json`, archive);

  if (purgeFromServer) {
    await purgeStudentFromServer(userId, emailLower, archive);
  }

  return archive;
}

async function purgeStudentFromServer(userId, emailLower, archive) {
  const batch = writeBatch(db);

  archive.subscriptions.forEach((s) => batch.delete(doc(db, 'subscriptions', s.id)));
  archive.payments.forEach((p) => batch.delete(doc(db, 'payments', p.id)));
  archive.messages.forEach((m) => batch.delete(doc(db, 'messages', m.id)));
  archive.notifications.forEach((n) => batch.delete(doc(db, 'notifications', n.id)));

  batch.delete(doc(db, 'userProgress', userId));
  batch.delete(doc(db, 'studyTracking', userId));
  batch.delete(doc(db, 'examAttempts', userId));
  if (archive.messageThread?.id) {
    batch.delete(doc(db, 'messageThreads', archive.messageThread.id));
  }
  batch.delete(doc(db, 'users', userId));

  await batch.commit();
}

/** Import a previously exported student archive (admin restores from file). */
export async function importStudentArchive(file) {
  const text = await file.text();
  const archive = JSON.parse(text);
  if (!archive?.user?.id) throw new Error('Invalid student export file.');

  const userId = archive.user.id;
  const { id: _id, ...userData } = archive.user;
  await setDoc(doc(db, 'users', userId), userData, { merge: true });

  for (const sub of archive.subscriptions || []) {
    const { id, ...data } = sub;
    await setDoc(doc(db, 'subscriptions', id), data, { merge: true });
  }
  for (const pay of archive.payments || []) {
    const { id, ...data } = pay;
    await setDoc(doc(db, 'payments', id), data, { merge: true });
  }
  if (archive.userProgress) {
    await setDoc(doc(db, 'userProgress', userId), archive.userProgress, { merge: true });
  }
  if (archive.studyTracking) {
    await setDoc(doc(db, 'studyTracking', userId), archive.studyTracking, { merge: true });
  }
  if (archive.examAttempts) {
    await setDoc(doc(db, 'examAttempts', userId), archive.examAttempts, { merge: true });
  }
  for (const msg of archive.messages || []) {
    const { id, ...data } = msg;
    await setDoc(doc(db, 'messages', id), data, { merge: true });
  }
  if (archive.messageThread) {
    const { id, ...data } = archive.messageThread;
    await setDoc(doc(db, 'messageThreads', id), data, { merge: true });
  }

  return { userId, email: archive.user.email };
}
